import { useCallback, useEffect, useState } from 'react';

import { Button } from '@components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '@components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select';
import { useAuth } from '@contexts/auth';
import errorsValidation from '@errors/validation';
import formatterOnlyNumber from '@formatters/onlyNumbers';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@lib/ui/cn';
import CustomerService from '@services/customers/CustomerService';
import { type IUpdateCustomerInputData } from '@services/customers/dtos/updateCustomer/InputData';
import type { IGetAllOccupationAreasOutputData } from '@services/occupationAreas/dtos/getAllOccupationAreas/OutputData';
import OccupationAreaService from '@services/occupationAreas/OccupationAreaService';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isAfter, parseISO, subDays } from 'date-fns';
import countries from 'i18n-iso-countries';
import Locales from 'i18n-iso-countries/langs/pt.json';
import { Check, ChevronsUpDown, LoaderCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { PatternFormat } from 'react-number-format';
import { toast } from 'sonner';
import { z } from 'zod';

import { RadioGroup, RadioGroupItem } from './ui/radio-group';

const formSchema = z
  .object({
    areaActivity: z.string().min(1, {
      message: errorsValidation.required,
    }),
    yearCompletion: z.string().min(1, {
      message: errorsValidation.required,
    }),
    document: z.string(),
    isGraduate: z.string().min(1, {
      message: errorsValidation.required,
    }),
    country: z.string().min(1, {
      message: errorsValidation.required,
    }),
  })
  .superRefine((val, ctx) => {
    if (val.country === 'Brasil' && !val.document) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: errorsValidation.required,
        path: ['document'],
      });
    }
  });

function CompleteInfoDialog() {
  const { onSessionInfo, user } = useAuth();
  const [years] = useState(() => {
    const years = [];
    for (let i = 1980; i < new Date().getFullYear() + 15; i++) {
      years.push({ label: i.toString(), value: i.toString() });
    }
    return years.reverse();
  });
  const [isOpen, setIsOpen] = useState<boolean>(false);

  countries.registerLocale(Locales);
  const countryNames = countries.getNames('pt', { select: 'official' });

  const { data: occupationAreas } = useQuery<IGetAllOccupationAreasOutputData>({
    queryFn: async () => {
      const response = await OccupationAreaService.getAll();
      return response;
    },
    queryKey: ['occupationArea'],
    refetchOnWindowFocus: false,
  });

  const { isPending, mutateAsync } = useMutation<
    void,
    unknown,
    IUpdateCustomerInputData
  >({
    mutationFn: CustomerService.update,
    onError: () => {
      toast.error('Atenção', {
        description:
          'Não foi possível atualizar as informações. Tente novamente',
      });
    },
    onSuccess: async () => {
      toast.success('Sucesso!', {
        description: 'Informações atualizadas com sucesso',
      });
      handleTrigger();
      await onSessionInfo();
    },
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    defaultValues: {
      areaActivity: occupationAreas?.list.find(
        (item) => item.name === user?.occupationArea,
      )
        ? occupationAreas.list
            .find((item) => item.name === user?.occupationArea)
            ?.id.toString()
        : '',
      yearCompletion: user?.yearGraduated || '',
      document: user?.document || '',
      isGraduate:
        Number(user?.yearGraduated) > new Date().getFullYear() ? '0' : '1',
      country: user?.country || '',
    },
  });

  const handleTrigger = useCallback(() => {
    setIsOpen((oldState) => !oldState);
    form.reset();
  }, [form]);

  const handleSubmit = useCallback(
    async (values: z.infer<typeof formSchema>) => {
      await mutateAsync({
        email: user!.email,
        gender: user!.gender,
        id: user!.id,
        name: user!.name,
        occupationArea: Number(values.areaActivity),
        occupationCity: user!.occupationCity!,
        occupationState: user!.occupationState!,
        phone: user!.phone,
        picture: user!.picture,
        yearGraduated: values.yearCompletion,
        document: formatterOnlyNumber(values.document),
        country: values.country,
      });
    },
    [mutateAsync, user],
  );

  useEffect(() => {
    if (
      !!user &&
      ((!user.document && user.country === 'Brasil') ||
        !user.yearGraduated ||
        !user.occupationArea ||
        !user.country) &&
      isAfter(subDays(new Date(), 1), parseISO(user.createdAt))
    ) {
      setIsOpen(true);
    }
  }, [user]);

  return (
    <Dialog open={isOpen}>
      <DialogContent
        aria-describedby={undefined}
        className="bg-primary border-primary sm:max-w-[425px] [&>button]:hidden">
        <DialogHeader>
          <DialogTitle className="text-background">
            Olá {user?.name}, tudo bem?
          </DialogTitle>
          <p className="text-background text-sm">
            Para deixar sua conta mais segura precisamos que você complete o
            cadastro das suas informações
          </p>
        </DialogHeader>
        <Form {...form}>
          <form
            className="space-y-6"
            onSubmit={form.handleSubmit(handleSubmit)}>
            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem className="flex w-full flex-col">
                  <div className="flex flex-row justify-between">
                    <FormLabel className="text-background">
                      País que reside
                    </FormLabel>
                    <FormMessage />
                  </div>

                  <Popover modal>
                    <PopoverTrigger asChild className="w-full">
                      <FormControl>
                        <Button
                          className={cn(
                            '[&_svg:not([class*="text-"])]:text-background hover:text-background aria-invalid:border-destructive text-background bg-primary aria-[expanded=true]:border-secondary aria-invalid:focus-visible:ring-0 w-full justify-between font-normal hover:bg-transparent aria-[expanded=true]:outline-1 aria-[expanded=true]:ring-2 [&_svg]:opacity-100',
                            !field.value && 'text-muted-foreground',
                          )}
                          role="combobox"
                          variant="outline">
                          {field.value
                            ? Object.values(countryNames)
                                .map((country) => ({
                                  label: country,
                                  value: country,
                                }))
                                .find((option) => option.value === field.value)
                                ?.label
                            : ''}
                          <ChevronsUpDown className="ml-auto size-4 shrink-0 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
                      <Command>
                        <CommandInput
                          className="placeholder:text-foreground/70 h-9"
                          placeholder="Buscar..."
                        />
                        <CommandList>
                          <CommandEmpty>Nenhuma opção encontrada.</CommandEmpty>
                          <CommandGroup>
                            {Object.values(countryNames)
                              .map((country) => ({
                                label: country,
                                value: country,
                              }))
                              .map((option) => (
                                <CommandItem
                                  className="data-[selected=true]:bg-tertiary"
                                  key={option.value}
                                  onSelect={() => {
                                    form.setValue(`country`, option.value, {
                                      shouldValidate: true,
                                    });
                                  }}
                                  value={option.value}>
                                  <PopoverClose className="flex w-full flex-row items-center">
                                    <Check
                                      className={cn(
                                        'mr-2 h-4 w-4',
                                        option.value === field.value
                                          ? 'opacity-100'
                                          : 'opacity-0',
                                      )}
                                    />
                                    {option.label}
                                  </PopoverClose>
                                </CommandItem>
                              ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="document"
              render={({ field: { ref, ...rest } }) => (
                <FormItem>
                  <div className="flex flex-row justify-between">
                    <FormLabel className="text-background">CPF</FormLabel>
                    <FormMessage />
                  </div>
                  <FormControl>
                    <PatternFormat
                      autoComplete="off"
                      className="[&_input]:border-background [&_input]:bg-primary [&_input]:text-background"
                      customInput={Input}
                      format="###.###.###-##"
                      getInputRef={ref}
                      mask="_"
                      {...rest}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="areaActivity"
              render={({ field }) => (
                <FormItem>
                  <div className="flex flex-row justify-between">
                    <FormLabel className="text-background">
                      Área de atuação
                    </FormLabel>
                    <FormMessage />
                  </div>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger
                        className="text-background [&_svg:not([class*='text-'])]:text-background [&_svg]:opacity-100"
                        onBlur={field.onBlur}>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>

                    <SelectContent>
                      {occupationAreas?.list.map((area) => (
                        <SelectItem
                          className="focus:bg-tertiary"
                          key={area.id}
                          value={area.id.toString()}>
                          {area.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isGraduate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-background">
                    Já está graduado?
                  </FormLabel>
                  <FormControl>
                    <RadioGroup
                      className="flex flex-row gap-x-4"
                      defaultValue={field.value}
                      onValueChange={field.onChange}>
                      <FormItem className="flex items-center">
                        <FormControl>
                          <RadioGroupItem
                            className="border-background data-[state=checked]:border-secondary [&_svg]:fill-secondary size-6 cursor-pointer border-2 transition-all duration-300 [&_svg]:size-5"
                            value="1"
                          />
                        </FormControl>
                        <FormLabel className="text-background">Sim</FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center">
                        <FormControl>
                          <RadioGroupItem
                            className="border-background data-[state=checked]:border-secondary [&_svg]:fill-secondary size-6 cursor-pointer border-2 transition-all duration-300 [&_svg]:size-5"
                            value="0"
                          />
                        </FormControl>
                        <FormLabel className="text-background">Não</FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="yearCompletion"
              render={({ field }) => (
                <FormItem className="flex w-full flex-col">
                  <div className="flex flex-row justify-between">
                    <FormLabel className="text-background">
                      {form.watch('isGraduate') === '1'
                        ? 'Em que ano se graduou?'
                        : 'Quando irá finalizar a graduação?'}
                    </FormLabel>
                    <FormMessage />
                  </div>
                  <Popover modal>
                    <PopoverTrigger asChild className="w-full">
                      <FormControl>
                        <Button
                          className={cn(
                            '[&_svg:not([class*="text-"])]:text-background hover:text-background aria-invalid:border-destructive text-background bg-primary aria-[expanded=true]:border-secondary aria-invalid:focus-visible:ring-0 w-full justify-between font-normal hover:bg-transparent aria-[expanded=true]:outline-1 aria-[expanded=true]:ring-2 [&_svg]:opacity-100',
                            !field.value && 'text-muted-foreground',
                          )}
                          role="combobox"
                          variant="outline">
                          {field.value
                            ? years.find(
                                (option) => option.value === field.value,
                              )?.label
                            : ''}
                          <ChevronsUpDown className="ml-auto size-4 shrink-0 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
                      <Command
                        filter={(value, search) => {
                          if (value.includes(search)) {
                            return 1;
                          }
                          return 0;
                        }}>
                        <CommandInput
                          className="placeholder:text-foreground/70 h-9"
                          placeholder="Buscar..."
                        />
                        <CommandList>
                          <CommandEmpty>Nenhuma opção encontrada.</CommandEmpty>
                          <CommandGroup>
                            {years.map((option) => (
                              <CommandItem
                                className="data-[selected=true]:bg-tertiary"
                                key={option.value}
                                onSelect={() => {
                                  form.setValue(
                                    `yearCompletion`,
                                    option.value,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                }}
                                value={option.value}>
                                <PopoverClose className="flex w-full flex-row items-center">
                                  <Check
                                    className={cn(
                                      'mr-2 h-4 w-4',
                                      option.value === field.value
                                        ? 'opacity-100'
                                        : 'opacity-0',
                                    )}
                                  />
                                  {option.label}
                                </PopoverClose>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </FormItem>
              )}
            />

            <DialogFooter className="flex justify-center sm:flex-col sm:justify-center">
              <Button
                className="w-full cursor-pointer"
                disabled={isPending}
                type="submit"
                variant="secondary">
                {isPending ? (
                  <LoaderCircle className="text-background animate-spin" />
                ) : (
                  'Confirmar'
                )}
              </Button>
              <Button
                className="w-full cursor-pointer"
                disabled={isPending}
                onClick={handleTrigger}
                type="button"
                variant="outline">
                Responder depois
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default CompleteInfoDialog;
